import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Target, Sparkles, TrendingUp } from "lucide-react";

/**
 * Student-facing milestone roadmap (iter64). Shows the currently ACTIVE
 * milestone as a headline aggregate progress bar + per-axis mini bars,
 * plus the coach-picked biggest-gap axis surfaced as "Next Focus".
 *
 * Renders nothing when the student has no active milestone.
 */
export default function MilestonesRoadmap({ studentId, sport, compact = false }) {
  const [milestone, setMilestone] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/students/${studentId}/milestones/active`, { params: { sport } })
      .then((r) => { if (!cancelled) setMilestone(r.data?.active || null); })
      .catch(() => { if (!cancelled) setMilestone(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId, sport]);

  if (loading) return null;
  if (!milestone) return null;

  const prog = milestone.progress || { per_axis: [], aggregate: 0, biggest_gap: null };
  const bg = prog.biggest_gap;
  const complete = bg && bg.gap <= 0;

  return (
    <div className={`card-surface p-6 fade-up ${compact ? "" : "border-l-4 border-[#CCFF00]"}`} data-testid="milestones-roadmap">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-[#CCFF00]" />
          <div>
            <div className="label-mono text-[10px] mb-1" style={{ color: "#CCFF00" }}>ACTIVE MILESTONE</div>
            {!compact && (
              <h2 className="h-display text-2xl tracking-tight">{milestone.name}</h2>
            )}
            {compact && (
              <div className="text-sm font-semibold">{milestone.name}</div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-[#A3A3A3]">Overall</div>
          <div className="h-display text-3xl tracking-tighter" style={{ color: complete ? "#CCFF00" : "#66C2FF" }}>
            {prog.aggregate}%
          </div>
        </div>
      </div>

      {/* Aggregate bar */}
      <div className="h-2 bg-[#141414] overflow-hidden mb-4">
        <div className="h-full transition-all" style={{ width: `${prog.aggregate}%`, background: complete ? "#CCFF00" : "linear-gradient(to right, #66C2FF, #CCFF00)" }} />
      </div>

      {/* Biggest-gap → Next Focus callout */}
      {bg && !complete && (
        <div className="bg-[#0A0A0A] border border-[#FF4500] p-3 mb-4" data-testid="milestone-next-focus">
          <div className="label-mono text-[10px] mb-1" style={{ color: "#FF4500" }}>
            <TrendingUp className="w-3 h-3 inline mr-1" /> NEXT FOCUS
          </div>
          <div className="text-sm">
            <strong>{bg.axis}</strong> — {bg.current == null ? "no data yet" : `currently ${bg.current}`} → target <strong>{bg.target}</strong>
            {bg.gap > 0 && <span className="text-[#A3A3A3]"> · gap {bg.gap}</span>}
          </div>
        </div>
      )}
      {complete && (
        <div className="bg-[#0A0A0A] border border-[#CCFF00] p-3 mb-4">
          <div className="label-mono text-[10px] mb-1" style={{ color: "#CCFF00" }}>
            <Sparkles className="w-3 h-3 inline mr-1" /> MILESTONE COMPLETE
          </div>
          <div className="text-sm">Every target hit. Ask your coach to set your next milestone.</div>
        </div>
      )}

      {/* Per-axis progress */}
      {!compact && (
        <div className="space-y-2">
          <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">Per-axis progress</div>
          {prog.per_axis.map((a) => (
            <div key={a.axis} data-testid={`milestone-axis-${a.axis}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <div className="font-semibold">{a.axis}</div>
                <div className="text-[#A3A3A3]">
                  {a.current == null ? "—" : a.current} / {a.target}
                  <span className="ml-2 font-bold" style={{ color: a.progress >= 100 ? "#CCFF00" : "#66C2FF" }}>
                    {a.progress}%
                  </span>
                </div>
              </div>
              <div className="h-1.5 bg-[#141414] overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${a.progress}%`, background: a.progress >= 100 ? "#CCFF00" : "#66C2FF" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
